import { gql, useMutation } from "@apollo/client"
import { LoadingButton } from "@mui/lab"
import { Box, Container, Stack, TextField } from "@mui/material"
import { Center, Flex, FlexColumn, InputPassword, Text } from "components/common"
import { DISCORD_CLIENT_ID } from "env"
import { useResponsive } from "hooks"
import { enqueueSnackbar } from "notistack"
import { DiscordLoginParams, useDiscordLogin } from "react-discord-login"
import { CodeResponse } from "react-discord-login/dist/DiscordLoginTypes"
import { Controller, useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { signIn } from "reducers/profileSlice"
import { authRoute } from "routes"

const LOGIN = gql(`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      accessToken,
      refreshToken,
      user {
        id
        name
        fullName
        username
        email
    personalEmail
        phoneNumber
        gender
        dob
        joinedCompanyAt
        position
        department
        employeeCode
        attendee {
          id
          name
          discordName
          discordAvatarUrl
          discordId
          isPartTime
        }
      }
    }
  }
`)

const LOGIN_DISCORD = gql(`
  mutation LoginWithDiscord($code: String!, $redirectUri: String!) {
    loginWithDiscord(code: $code, redirectUri: $redirectUri) {
      accessToken,
      refreshToken,
      user {
        id
        name
        fullName
        username
        email
    personalEmail
        phoneNumber
        gender
        dob
        joinedCompanyAt
        position
        department
        employeeCode
        attendee {
          id
          name
          discordName
          discordAvatarUrl
          discordId
          isPartTime
        }
      }
    }
  }
`)

type LoginFormValues = {
  password: string
  username: string
}

const LoginScreen = () => {
  const dispatch = useDispatch()
  const { isDesktop } = useResponsive()

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<LoginFormValues>({
    defaultValues: {
      password: "",
      username: "",
    },
  })

  const [login] = useMutation(LOGIN)
  const [loginWithDiscord, { loading }] = useMutation(LOGIN_DISCORD)

  const onSubmit = async (values: LoginFormValues) => {
    const { username } = values
    await login({
      variables: {
        ...values,
        username: username.trim().toLowerCase(),
      },
    }).then((response) => {
      if (response.data) {
        enqueueSnackbar("Đăng nhập thành công")

        const loginData = response.data.login
        dispatch(signIn(loginData))
      }
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)()
    }
  }

  const discordLoginParams: DiscordLoginParams = {
    clientId: DISCORD_CLIENT_ID,
    redirectUri: window.location.origin + window.location.pathname,
    responseType: "code",
    scopes: ["identify", "email"],
  }

  const { buildUrl } = useDiscordLogin({
    ...discordLoginParams,
    onSuccess: (response) => {
      const { code } = response as CodeResponse
      loginWithDiscord({
        variables: {
          code,
          redirectUri: discordLoginParams.redirectUri!,
        },
      }).then((response) => {
        if (response.data) {
          enqueueSnackbar("Đăng nhập thành công")

          const loginData = response.data.loginWithDiscord
          dispatch(signIn(loginData))
        }
      })
    },
  })

  return (
    <Container maxWidth="sm">
      <Box
        className="fixed left-0 right-0 top-0 h-[480px] lg:left-[5%] lg:right-[5%] lg:top-[2%] lg:rounded-3xl"
        sx={{ background: `url(${require("assets/images/Airdata_banner_2026.jpg")}) no-repeat center / cover` }}
      />
      <Stack className="relative z-10 gap-6 rounded-3xl bg-white p-6 lg:p-12">
        <Center>
          <img className="h-[60px]" src={require("assets/icons/Airdata_logo.png")} />
        </Center>
        <Stack>
          <Text className="text-center text-xl font-bold">{isDesktop && "Chào mừng đến với "}Airdata Company</Text>
          <Text className="text-center font-bold text-primary-main">Vui lòng đăng nhập vào tài khoản của bạn</Text>
        </Stack>

        <FlexColumn className="items-center justify-center gap-6">
          <Controller
            control={control}
            name="username"
            render={({ field, fieldState: { error } }) => (
              <TextField
                autoComplete="username"
                fullWidth
                label="Tên đăng nhập"
                required
                {...field}
                error={!!error}
                helperText={error?.message}
                onKeyDown={handleKeyDown}
              />
            )}
            rules={{
              required: "Tên đăng nhập không được để trống",
            }}
          />

          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <InputPassword
                autoComplete="current-password"
                fullWidth
                label="Mật khẩu"
                required
                {...field}
                error={!!error}
                helperText={error?.message}
                onKeyDown={handleKeyDown}
              />
            )}
            rules={{
              required: "Mật khẩu không được để trống",
            }}
          />

          <LoadingButton
            fullWidth
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            size="large"
            variant="contained"
          >
            Đăng nhập
          </LoadingButton>

          <LoadingButton
            color="secondary"
            fullWidth
            loading={loading}
            onClick={() => (window.location.href = buildUrl())}
            size="large"
            startIcon={<img className="w-6" src={require("assets/icons/Discord.svg").default} />}
            variant="contained"
          >
            Đăng nhập bằng Discord
          </LoadingButton>

          <Flex gap={1}>
            Chưa có tài khoản?
            <Link className="font-bold text-primary-main hover:brightness-75" to={authRoute.register.url}>
              Đăng ký
            </Link>
          </Flex>
        </FlexColumn>
      </Stack>
    </Container>
  )
}

export default LoginScreen
